import { useState, useEffect } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import {
  Card,
  CardContent,
  Avatar,
  Chip,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  Backdrop
} from "@mui/material"
import {
  Crown,
  Sparkles,
  TrendingUp,
  Zap,
  Globe,
  Shield,
  Search,
  Settings,
  User,
  LogOut,
  X,
  ChevronRight,
  Star,
  Diamond,
  Flame,
  Target,
  Activity,
  Layers,
  BarChart3,
  PieChart,
  Briefcase,
  Wallet,
  CreditCard,
  Gift,
  Users,
  MessageSquare,
  Calendar,
  FileText,
  Download,
  Upload,
  Eye,
  Heart,
  Bookmark,
  Share2,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  Minus,
  MoreVertical,
  Menu,
  DollarSign,
  Percent,
  Clock,
  UserCheck,
  TrendingDown,
  Home,
  LineChart,
  PlusCircle,
  Bell,
  RefreshCw,
  Banknote,
  UserCircle,
  Cog,
  Award,
  HandCoins
} from "lucide-react"
import { capitalizeFirstLetter, guessAdminRoute } from "../../../../utility/Helper1"
import { useDispatch, useSelector } from "react-redux"
import { allRemoteSettingsSelect, homeScreenStatSelect, userSelect } from "../../../../redux/selectors"
import { authActions, exchangeActions, peerActions } from "../../../../redux/actions"

export default function WrapUpP2P({ children, allowAutoPadding = true }) {

  const navigate = useNavigate();
  const location = useLocation();
  const userData = useSelector(userSelect);
  const site_settings = useSelector(allRemoteSettingsSelect)
  const finance = useSelector(homeScreenStatSelect)
  const [isBalanceVisible, setIsBalanceVisible] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch();

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [activeSection, setActiveSection] = useState("analytics")
  const [searchQuery, setSearchQuery] = useState("")
  const [isMobile, setIsMobile] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [p2pStats, setP2pStats] = useState({
    totalInvested: 245000,
    activeLoans: 127,
    avgReturn: 12.8,
    pendingReturns: 18500,
    monthlyIncome: 3250,
    portfolioGrowth: 23.5,
    riskScore: 4.2,
    successRate: 94.7
  })

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
      // Auto-close sidebar on mobile, auto-open on desktop
      if (window.innerWidth < 768) {
        setSidebarOpen(false)
      } else {
        setSidebarOpen(true)
      }
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // auto loaders
  const onLoadPH = () => { dispatch(exchangeActions.onGetExchange("p2p/load/all", response => { })) }
  const onLoadPeer = () => { dispatch(peerActions.onGet("p2p/load/peer", response => { setIsLoading(false) })) }

  const loadAllDashboard = () => {
    onLoadPH()
    onLoadPeer()
  }

  useEffect(() => {
    loadAllDashboard()
  }, [])

  const refreshData = () => {
    setIsLoading(true)
    loadAllDashboard()
  }

  // Simulate real-time data updates
  useEffect(() => {
    const interval = setInterval(() => {
      setP2pStats(prev => ({
        ...prev,
        pendingReturns: prev.pendingReturns + Math.floor(Math.random() * 100),
        monthlyIncome: prev.monthlyIncome + Math.floor(Math.random() * 50),
        avgReturn: prev.avgReturn + (Math.random() - 0.5) * 0.1
      }))
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  // Mobile tab navigation - updated with proper routes
  const mobileNavItems = [
    { name: "Home", icon: <Home size={20} />, to: 'dashboard', id: "dashboard" },
    { name: "Peering", icon: <HandCoins size={20} />, to: 'peerings', id: "peerings" },
    { name: "Bids", icon: <LineChart size={20} />, to: 'all/deposit/withdrawal', id: "bids" },
    { name: "Wallet", icon: <Wallet size={20} />, to: 'withdraw/earning', id: "wallet" },
    { name: "Profile", icon: <UserCircle size={20} />, to: 'view/my/profile', id: "profile" }
  ]

  // Navigation items with golden theme - updated with better icons and proper routes
  const navigationItems = [
    {
      section: "Overview",
      items: [
        { name: "Dashboard", to: 'dashboard', icon: <Home size={20} />, id: "dashboard", color: "from-amber-400 to-yellow-600" },
        { name: "Bid", to: 'new/bid', icon: <PlusCircle size={20} />, id: "bid", color: "from-orange-400 to-amber-600" },
        // { name: "Performance", to: 'performance', icon: <TrendingUp size={20} />, id: "performance", color: "from-yellow-400 to-orange-600" }
      ]
    },
    {
      section: "Investment",
      items: [
        { name: "Peerings", to: 'peerings', icon: <HandCoins size={20} />, id: "peerings", color: "from-amber-500 to-yellow-700" },
        // { name: "Portfolio", to: 'portfolio', icon: <Briefcase size={20} />, id: "portfolio", color: "from-amber-500 to-yellow-700" },
        { name: "Transactions", to: 'all/deposit/withdrawal', icon: <LineChart size={20} />, id: "transactions", color: "from-yellow-500 to-amber-700" },
        { name: "Withdraw", to: 'withdraw/earning', icon: <Banknote size={20} />, id: "withdrawals", color: "from-orange-500 to-yellow-700" }
      ]
    },
    {
      section: "Account",
      items: [
        { name: "Profile", to: 'view/my/profile', icon: <UserCircle size={20} />, id: "profile", color: "from-amber-400 to-orange-600" },
        { name: "Wallet", to: 'withdraw/earning', icon: <Wallet size={20} />, id: "wallet", color: "from-yellow-400 to-amber-600" },
        { name: "Referrals", to: 'my/referral', icon: <Award size={20} />, id: "referrals", color: "from-orange-400 to-yellow-600" }
      ]
    }
  ]

  // Update active tab based on current route
  useEffect(() => {
    const currentPath = location.pathname.replace('/', '');
    
    // Find matching mobile nav item
    const mobileNavIndex = mobileNavItems.findIndex(item => 
      currentPath === item.to || currentPath.startsWith(item.to)
    );
    
    if (mobileNavIndex !== -1) {
      setActiveTab(mobileNavIndex);
      setActiveSection(mobileNavItems[mobileNavIndex].id);
    }

    // Find matching sidebar item
    const sidebarItem = navigationItems
      .flatMap(section => section.items)
      .find(item => currentPath === item.to || currentPath.startsWith(item.to));
    
    if (sidebarItem) {
      setActiveSection(sidebarItem.id);
    }
  }, [location.pathname]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen)
  }

  const handleNavItemClick = (itemId, itemTo) => {
    setActiveSection(itemId)
    if (itemTo) {
      navigate(`/${itemTo}`)
    }
    // Close sidebar on mobile after selection
    if (isMobile) {
      setSidebarOpen(false)
    }
  }

  const handleTabClick = (index, item) => {
    navigate(`/${item.to}`)
    setActiveTab(index)
    setActiveSection(item.id)
  }

  const SidebarContent = () => (
    <div className="h-full flex flex-col bg-gradient-to-br from-slate-950 via-gray-900 to-slate-900">
      {/* Sidebar Header */}
      <div className="p-6 border-b border-amber-900/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
            <Avatar
              src={`${guessAdminRoute()}/${site_settings?.site_settings?.logo}`}
              className="h-8 w-8 flex-shrink-0"
            />
            </div>
            <div>
              <Typography variant="h6" className="text-white font-bold">
                {site_settings?.site_settings?.sitename}
              </Typography>
              <Typography variant="caption" className="text-amber-400">
                Premium Platform
              </Typography>
            </div>
          </div>
          {isMobile && (
            <button
              onClick={handleSidebarToggle}
              className="relative p-2 rounded-lg bg-gradient-to-r from-amber-400/20 to-yellow-500/20 border border-amber-400/30 hover:border-amber-400/50 hover:from-amber-400/30 hover:to-yellow-500/30 transition-all duration-300 group"
            >
              <X size={18} className="text-amber-400 group-hover:text-amber-300 transition-colors duration-300" />
              <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-amber-400/0 to-yellow-500/0 group-hover:from-amber-400/10 group-hover:to-yellow-500/10 transition-all duration-300"></div>
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 py-4 overflow-y-auto">
        {navigationItems.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-6">
            <Typography variant="overline" className="text-amber-400/70 px-6 mb-3 block text-xs font-semibold">
              {section.section}
            </Typography>
            <List className="space-y-1 px-3">
              {section.items.map((item, itemIndex) => (
                <ListItem
                  key={itemIndex}
                  component={item.to ? Link : "div"}
                  to={item.to ? `/${item.to}` : undefined}
                  className={`rounded-xl transition-all duration-300 cursor-pointer group hover:bg-gradient-to-r hover:${item.color} hover:shadow-lg hover:shadow-amber-500/20 ${activeSection === item.id
                    ? `bg-gradient-to-r ${item.color} shadow-lg shadow-amber-500/30`
                    : 'hover:bg-white/5'
                    }`}
                  onClick={() => handleNavItemClick(item.id, item.to)}
                >
                  <ListItemIcon className={`transition-colors duration-300 min-w-0 mr-3 ${activeSection === item.id ? 'text-black' : 'text-amber-400 group-hover:text-black'
                    }`}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      className: `font-medium transition-colors duration-300 ${activeSection === item.id ? 'text-black' : 'text-white group-hover:text-black'
                        }`
                    }}
                  />
                  <ChevronRight size={16} className={`transition-all duration-300 ${activeSection === item.id ? 'text-black opacity-100' : 'text-amber-400 opacity-0 group-hover:opacity-100 group-hover:text-black'
                    }`} />
                </ListItem>
              ))}
            </List>
          </div>
        ))}
      </div>

      {/* Sidebar Footer */}
      {/* <div className="p-4 border-t border-amber-900/20">
        <div className="flex items-center justify-between">
          {!isMobile && (
            <button
              onClick={handleSidebarToggle}
              className="relative p-2.5 rounded-xl bg-gradient-to-r from-amber-400/10 to-yellow-500/10 border border-amber-400/20 hover:border-amber-400/40 hover:from-amber-400/20 hover:to-yellow-500/20 transition-all duration-300 group hover:shadow-md hover:shadow-amber-500/20"
            >
              <div className="w-5 h-5 flex flex-col justify-center items-center space-y-0.5">
                <div className={`w-4 h-0.5 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full transition-all duration-300 ${sidebarOpen ? 'rotate-45 translate-y-1' : 'group-hover:w-5'
                  }`}></div>
                <div className={`w-3 h-0.5 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full transition-all duration-300 ${sidebarOpen ? 'opacity-0' : 'group-hover:w-4 opacity-100'
                  }`}></div>
                <div className={`w-4 h-0.5 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full transition-all duration-300 ${sidebarOpen ? '-rotate-45 -translate-y-1' : 'group-hover:w-5'
                  }`}></div>
              </div>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-amber-400/0 to-yellow-500/0 group-hover:from-amber-400/5 group-hover:to-yellow-500/5 transition-all duration-300"></div>
            </button>
          )}
          <div className="flex space-x-2 ml-auto">
            <IconButton
              component={Link}
              to="/my/settings"
              className="text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
            >
              <Settings size={18} />
            </IconButton>
            <IconButton className="text-red-400 hover:text-red-300 hover:bg-red-400/10">
              <LogOut size={18} />
            </IconButton>
          </div>
        </div>
      </div> */}
      
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Floating Particles Effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-2 h-2 bg-amber-400 rounded-full animate-pulse opacity-30"></div>
        <div className="absolute top-40 right-32 w-1 h-1 bg-yellow-400 rounded-full animate-ping opacity-40"></div>
        <div className="absolute bottom-32 left-16 w-3 h-3 bg-orange-400 rounded-full animate-bounce opacity-20"></div>
        <div className="absolute top-60 left-1/2 w-1 h-1 bg-amber-300 rounded-full animate-pulse opacity-50"></div>
      </div>

      {/* Mobile Backdrop */}
      {isMobile && sidebarOpen && (
        <Backdrop
          open={sidebarOpen}
          onClick={handleSidebarToggle}
          className="z-40"
          sx={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
        />
      )}

      {/* Sidebar */}
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={sidebarOpen}
        onClose={isMobile ? handleSidebarToggle : undefined}
        ModalProps={isMobile ? {
          keepMounted: true, // Better open performance on mobile
        } : undefined}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            border: 'none',
            borderRight: '1px solid rgba(251, 191, 36, 0.1)',
            background: 'transparent',
            overflow: 'hidden'
          }
        }}
      >
        <SidebarContent />
      </Drawer>

      {/* Main Content */}
      <div className={`transition-all duration-300 ease-in-out ${sidebarOpen && !isMobile ? 'ml-[280px]' : 'ml-0'
        } ${isMobile ? 'pb-20' : ''}`}>

        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-gray-900 to-slate-900">
          {/* Sophisticated Header */}
          {allowAutoPadding && <>
            <div className="relative border-b border-slate-800/50 backdrop-blur-sm">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-yellow-500/5 to-amber-600/5"></div>
              <div className="relative max-w-7xl mx-auto px-6 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    {/* Mobile Menu Button */}
                    {isMobile && (
                      <button
                        onClick={handleSidebarToggle}
                        className="p-2 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:bg-slate-700/40 transition-all duration-200 backdrop-blur-sm"
                      >
                        <Menu className="h-5 w-5 text-slate-300" />
                      </button>
                    )}
                    <div>
                      <h1 className="text-2xl font-light text-white">
                        <span className="font-medium bg-gradient-to-r from-amber-200 to-yellow-400 bg-clip-text text-transparent">{capitalizeFirstLetter(userData?.username || 'Investor')}</span>
                      </h1>
                      <p className="text-slate-400 text-sm mt-1 font-light">
                        Portfolio Management • Investment Analytics • Wealth Growth
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={refreshData}
                      disabled={isLoading}
                      className="p-2.5 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:bg-slate-700/40 transition-all duration-200 backdrop-blur-sm"
                    >
                      <RefreshCw className={`h-4 w-4 text-slate-300 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>

                    <button onClick={() => navigate('/view/my/profile')} className="p-2.5 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:bg-slate-700/40 transition-all duration-200 backdrop-blur-sm">
                      <Settings className="h-4 w-4 text-slate-300" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
              {children}
            </div>
          </>}

          {!allowAutoPadding && <>
            {children}
          </>}
        </div>
      </div>

      {/* Mobile Bottom Tab Navigation */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-br from-slate-950 via-gray-900 to-slate-900 border-t border-slate-800/50">
          <div className="flex items-center justify-around py-2">
            {mobileNavItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleTabClick(index, item)}
                className={`relative flex flex-col items-center justify-center p-3 rounded-2xl transition-all duration-300 group ${activeTab === index ? 'transform -translate-y-1' : 'hover:transform hover:-translate-y-0.5'
                  }`}
              >
                {/* Active tab background */}
                <div className={`absolute inset-0 rounded-2xl transition-all duration-300 ${activeTab === index
                  ? 'bg-gradient-to-r from-amber-400 to-yellow-600 shadow-lg shadow-amber-500/30'
                  : 'bg-gradient-to-r from-amber-400/0 to-yellow-600/0 group-hover:from-amber-400/10 group-hover:to-yellow-600/10'
                  }`}></div>

                {/* Pulsing ring for active tab */}
                {activeTab === index && (
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-amber-400 to-yellow-600 animate-ping opacity-20"></div>
                )}

                {/* Icon */}
                <div className={`relative z-10 transition-all duration-300 ${activeTab === index ? 'text-black' : 'text-amber-400 group-hover:text-amber-300'
                  }`}>
                  {item.icon}
                </div>

                {/* Label */}
                <Typography
                  variant="caption"
                  className={`relative z-10 text-xs font-medium transition-all duration-300 mt-1 ${activeTab === index ? 'text-black' : 'text-amber-400 group-hover:text-amber-300'
                    }`}
                >
                  {item.name}
                </Typography>

                {/* Active indicator dot */}
                {activeTab === index && (
                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-black rounded-full"></div>
                )}
              </button>
            ))}
          </div>

          {/* Bottom safe area for iPhone */}
          <div className="h-safe-bottom bg-black/50"></div>
        </div>
      )}
    </div>
  )
}